import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/auth';
import { api } from '../lib/api';
import LanguageSelector from '../components/LanguageSelector';
import { AlertTriangle } from 'lucide-react';

export default function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [stripeReady, setStripeReady] = useState<{ ready: boolean; reason?: string } | null>(null);

  // X1-AFF-5: capture an inbound referral code (?ref=CODE) from the landing
  // page and forward it to the register endpoint so the signup is attributed
  // to the referring partner. Persist to localStorage so it survives the
  // navigation from the landing page to /register.
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || localStorage.getItem('affiliateReferralCode') || '';
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) localStorage.setItem('affiliateReferralCode', ref);
  }, [searchParams]);

  useEffect(() => {
    const checkStripeStatus = async () => {
      try {
        const res = await api.get('/api/v1/public/stripe-status');
        setStripeReady(res.data);
      } catch {
        setStripeReady({ ready: false, reason: 'Unable to check status' });
      }
    };
    checkStripeStatus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload: Record<string, unknown> = { ...formData };
      if (referralCode) payload.referralCode = referralCode;
      const response = await api.post('/api/v1/auth/register', payload);
      const { accessToken, refreshToken, user } = response.data;
      login(user, accessToken, refreshToken);
      navigate('/affiliate/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Show warning if Stripe is not ready
  const isStripeNotReady = stripeReady ? !stripeReady.ready : false;
  const showWarning = isStripeNotReady;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full p-8 bg-white rounded-lg shadow-md">
        <div className="flex justify-end mb-4">
            <LanguageSelector />
        </div>
        <h1 className="text-2xl font-bold text-center mb-6">{t('auth.register')}</h1>

        {referralCode && (
          <p className="mb-4 text-center text-sm text-blue-600">
            {t('affiliateLanding.referredNote')}
          </p>
        )}
        
        {showWarning && stripeReady && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800 font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {t('auth.registrationUnavailable')}
            </p>
            <p className="text-xs text-yellow-700 mt-1">
              {stripeReady.reason || 'The platform is not ready to accept new affiliates yet. Please try again later.'}
            </p>
          </div>
        )}
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <input
              type="text"
              placeholder={t('affiliates.firstName')}
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              disabled={showWarning}
            />
            <input
              type="text"
              placeholder={t('affiliates.lastName')}
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              disabled={showWarning}
            />
            <input
              type="email"
              placeholder={t('auth.email')}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
              disabled={showWarning}
            />
            <input
              type="password"
              placeholder={t('auth.password')}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
              disabled={showWarning}
            />
            <button
              type="submit"
              disabled={loading || showWarning}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:bg-gray-400"
            >
              {loading ? t('common.loading') : showWarning ? 'Registration Closed' : t('auth.register')}
            </button>
          </div>
        </form>
        <p className="mt-4 text-center text-sm text-gray-600">
          {t('auth.hasAccount')}{' '}
          <Link to="/login" className="text-blue-600 hover:underline">
            {t('auth.login')}
          </Link>
        </p>
      </div>
    </div>
  );
}
